import React, { useEffect, useState, Fragment } from 'react';
import { FlameIcon, AlertTriangleIcon } from 'lucide-react';
const DURATION_MS = 30 * 60 * 1000; // 30 minutes
const STORAGE_KEY = 'kco_oferta_deadline';
function getDeadline(): number {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  const now = Date.now();
  if (stored) {
    const value = parseInt(stored, 10);
    if (!Number.isNaN(value) && value > now) return value;
  }
  // Evergreen: (re)start a fresh 30-minute window
  const fresh = now + DURATION_MS;
  window.localStorage.setItem(STORAGE_KEY, String(fresh));
  return fresh;
}
function useCountdown() {
  const [deadline, setDeadline] = useState<number>(() => getDeadline());
  const [remaining, setRemaining] = useState<number>(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const tick = () => {
      const left = deadline - Date.now();
      if (left <= 0) {
        // Reset the window so urgency persists
        const fresh = getDeadline();
        setDeadline(fresh);
        setRemaining(Math.max(0, fresh - Date.now()));
      } else {
        setRemaining(left);
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return remaining;
}
const pad = (n: number) => String(n).padStart(2, '0');
export function UrgencyBanner() {
  const remaining = useCountdown();
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  // Progress bar: fills up as time runs out (cupos llenándose)
  const pct = Math.min(100, Math.max(0, (DURATION_MS - remaining) / DURATION_MS * 100));
  return <div className="mx-auto mb-5 max-w-[760px] overflow-hidden rounded-xl border-2 border-brandRed bg-gradient-to-r from-[#2a0a0e] via-[#3d0d12] to-[#2a0a0e] shadow-[0_8px_32px_rgba(230,57,70,0.35)]" data-id="element-240">
      <div className="flex flex-col items-center gap-2.5 p-3.5 sm:flex-row sm:gap-4 sm:p-4" data-id="element-241">
        {/* Pulsing flame + message */}
        <div className="flex items-center gap-2.5 text-center sm:text-left" data-id="element-242">
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center" data-id="element-243">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brandRed/40" data-id="element-244" />
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-brandRed text-white" data-id="element-245">
              <FlameIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-246" />
            </span>
          </span>
          <p className="font-montserrat text-[12px] font-extrabold uppercase leading-tight tracking-[0.5px] text-white sm:text-[13px]" data-id="element-247">
            ¡Últimos cupos a este precio!
          </p>
        </div>

        {/* Countdown */}
        <div role="timer" aria-live="off" className="flex items-center gap-1.5 sm:ml-auto" data-id="element-248">
          {[{
          v: pad(minutes),
          l: 'MIN'
        }, {
          v: pad(seconds),
          l: 'SEG'
        }].map((unit, i) => <Fragment key={unit.l} data-id="element-249">
              {i > 0 && <span className="font-bebas text-[26px] leading-none text-gold" data-id="element-250">
                  :
                </span>}
              <div className="flex flex-col items-center" data-id="element-251">
                <span className="min-w-[42px] rounded-md bg-black/40 px-2 py-1 text-center font-bebas text-[28px] leading-none text-white tabular-nums" data-id="element-252">
                  {unit.v}
                </span>
                <span className="mt-0.5 font-montserrat text-[8px] font-bold tracking-[1px] text-white/50" data-id="element-253">
                  {unit.l}
                </span>
              </div>
            </Fragment>)}
        </div>
      </div>

      {/* Progress bar (cupos filling up) */}
      <div className="px-3.5 pb-2 sm:px-4" data-id="element-254">
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/10" data-id="element-255">
          <div className="h-full rounded-full bg-gradient-to-r from-gold to-brandRed transition-[width] duration-1000 ease-linear" style={{
          width: `${pct}%`
        }} data-id="element-256" />
        </div>
      </div>

      {/* Warning line */}
      <div className="flex items-center justify-center gap-1.5 bg-brandRed px-3 py-2 text-center" data-id="element-257">
        <AlertTriangleIcon className="h-3.5 w-3.5 shrink-0 text-white" aria-hidden="true" data-id="element-258" />
        <p className="font-montserrat text-[11px] font-bold leading-tight text-white sm:text-[12px]" data-id="element-259">
          Cuando se agoten los cupos el precio sube{' '}
          <span className="text-gold-light" data-id="element-260">$10.000</span> y no vuelve a bajar.
        </p>
      </div>
    </div>;
}